import { useState } from 'react'
import { Link } from 'react-router-dom'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote, DesignNote } from './designNotes'
import LevelPill from '../../components/badges/LevelPill'
import PageMasthead from '../../components/PageMasthead'
import TabBar from './components/TabBar'
import { WIREFRAME_PATHS } from './routes'
import { PROFILE_OVERVIEW } from './mockData'
import OverviewTab from './components/profileConfig/OverviewTab'
import UsersTab from './components/profileConfig/UsersTab'
import ClassifiersTab from './components/profileConfig/ClassifiersTab'
import AutoModerationTab from './components/profileConfig/AutoModerationTab'
import KeywordsTab from './components/profileConfig/KeywordsTab'
import AiRepliesTab from './components/profileConfig/AiRepliesTab'
import ContentFetchingTab from './components/profileConfig/ContentFetchingTab'
import AdminTab from './components/profileConfig/AdminTab'

type Tab =
    | 'Overview'
    | 'Users'
    | 'Classifiers'
    | 'Auto-moderation'
    | 'Keywords'
    | 'AI & replies'
    | 'Content & fetching'
    | 'Admin'

const TABS: Tab[] = [
    'Overview',
    'Users',
    'Classifiers',
    'Auto-moderation',
    'Keywords',
    'AI & replies',
    'Content & fetching',
    'Admin'
]

export default function ProfileConfigPage({ initialTab = 'Overview' }: { initialTab?: Tab }) {
    const [activeTab, setActiveTab] = useState<Tab>(initialTab)

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote
                replaces="Profile settings"
                reachableFrom="the existing Profile settings page"
            >
                Consolidates the scattered profile settings into one tabbed page — overview,
                classifiers, and users & access in a single place.
            </PageDesignNote>

            <PageMasthead
                breadcrumb={
                    <>
                        <Link to={WIREFRAME_PATHS.orgList} className="text-arwen-700 hover:underline">
                            Organisations
                        </Link>{' '}
                        /{' '}
                        <Link to={WIREFRAME_PATHS.orgConfig} className="text-arwen-700 hover:underline">
                            Acme Corp
                        </Link>{' '}
                        /{' '}
                        <Link to={WIREFRAME_PATHS.teamConfig} className="text-arwen-700 hover:underline">
                            Acme — Social
                        </Link>
                    </>
                }
                title={
                    <>
                        @AcmeUK · YouTube{' '}
                        <DesignNote n={1} type="question">
                            Should the profile header surface the connected account avatar and a
                            quick link back to the platform here?
                        </DesignNote>
                    </>
                }
                levelPill={<LevelPill level="profile" />}
                subtitle={`${PROFILE_OVERVIEW.platform} · ${PROFILE_OVERVIEW.totalComments.toLocaleString()} comments`}
            />

            <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} scrollable />

            {/* Tab content */}
            <div className="flex-1 overflow-auto px-6 py-5">
                <div className="max-w-2xl">
                    {activeTab === 'Overview' && <OverviewTab />}
                    {activeTab === 'Users' && <UsersTab />}
                    {activeTab === 'Classifiers' && <ClassifiersTab />}
                    {activeTab === 'Auto-moderation' && <AutoModerationTab />}
                    {activeTab === 'Keywords' && <KeywordsTab />}
                    {activeTab === 'AI & replies' && <AiRepliesTab />}
                    {activeTab === 'Content & fetching' && <ContentFetchingTab />}
                    {activeTab === 'Admin' && <AdminTab />}
                </div>
            </div>
        </div>
    )
}
